
import { Component, Vue, Watch, Prop, Emit } from "vue-property-decorator"
import { namespace, Getter } from "vuex-class"

import { AdvancedSearchFilter, CaseDetails, Field } from "@/types"
import User from "@/types/user"
import { FormattingRule, InsightCalculationTotal, LabelFormat, Insight, InsightCalculation, Operator, DataType } from "@/types/insights"
import { CaseColumn } from "@/types/caseColumns"

// @ts-ignore
import VuePerfectScrollbar from "vue-perfect-scrollbar"
import CaseTable from "@/components/CaseList/CaseTable.vue"
import ColumnFilters from "@/components/Search/ColumnFilters.vue"
import { UserSettings } from "@/types/userSettings"

import api from "@/services/api"

import { filterMixin } from "@/mixins/filtering"
import { CalculationResults, FilteredCalculationResults } from "@/types/insights/calculatedResults"
import { Schedule } from "@/types/schedule"
import { applyFieldChanges } from "@/scripts/caseDetails"
import { formatInsight } from "@/mixins/FormatInsight"
import { CurrentPageDetails } from "@/types/paginatedRequest"
import { Clause } from "@/types/view"
import { isEqual } from "lodash"
import TableCalculations from "./Calculations/TableCalculations.vue"

const setup = namespace("setup")

@Component({
	components: {
		"scrollbar": VuePerfectScrollbar,
		"case-table": CaseTable,
		"column-filters": ColumnFilters,
		"schedule-popup": () => import("@/components/Config/Schedules/ScheduleInstance.vue"),
		"table-calculations": TableCalculations
	}
})
export default class TableResults extends Vue {
	@Getter("getMe") public me!: User;

	@Getter("getScreenWidth") public screenWidth!: number;

	@Getter("getUserSettings")
	public userSettings!: UserSettings;

	@setup.State
	fields!: Field[];

	@Prop()
	resultGroups!: readonly FilteredCalculationResults[];

	@Prop()
	columns!: any[];

	@Prop()
	allCaseFields!: CaseColumn[];

	@Prop({ required: true })
	insight!: Insight

	@Prop()
	calculations: any;

	@Prop()
	calculationGroupByLabels: any;

	@Prop({ default: LabelFormat.Text })
	labelFormat!: LabelFormat;

	@Prop({ default: true })
	showCalculations!: boolean;

	@Prop({ default: false })
	showFilters!: boolean;

	@Prop({ type: Number, default: 20 })
	perPage!: number;

	@Prop({ default: () => [] })
	additionalFilters!: AdvancedSearchFilter[]

	@Prop({ type: Number, default: 0 })
	resultsUpdateHook!: number;

	@Prop({ default: () => [] })
	loadingPageTableIds!: string[];

	@Prop({ type: Boolean, default: false })
	compact!: boolean;

	private filters: AdvancedSearchFilter[] = [];
	filterFunctions: ((record: CaseDetails) => boolean)[] = [];

	private groupPages: { [key: string]: CurrentPageDetails } = {};
	private orClauseKey: string = "or-1";

	private viewScheduleInstance: {
		schedule: Schedule,
		date: Date
	} | null = null

	private get tableCalculations () : InsightCalculation[] {
		if (this.calculations) {
			return this.calculations
		}
		return this.insight?.calculations ?? []
	}

	private get formattingRules () : { [key: string]: FormattingRule[] } {
		return this.insight?.formattingRules || {}
	}

	private get calculationTotals () : InsightCalculationTotal[] {
		return this.insight?.calculationTotals || []
	}

	private get filterColumnGroups (): CaseColumn[][] {
		return [
			this.columns
		]
	}

	private get filteredResultGroups () : FilteredCalculationResults[] {
		const reactivityHook = this.resultsUpdateHook
		return filterMixin.methods.filterGroups((this.resultGroups as FilteredCalculationResults[]),
			this.filterFunctions, this.labelFormat, this.insight, this.fields
		).filter(g => g.results.length > 0)
	}

	private get filtered () : boolean {
		return (this.filters && this.filters.length > 0) || (this.filterFunctions && this.filterFunctions.length > 0)
	}

	private beforeMount (): void {
		this.setGroupPages()
	}

	private mounted (): void {
		if (this.additionalFilters && this.additionalFilters.length > 0) {
			this.applyFilters()
		} else {
			this.resultGroupsChanged()
		}
	}

	private setGroupPages (): void {
		this.groupPages = {}

		if (!!this.resultGroups && this.resultGroups.length > 0) {
			this.groupPages = this.resultGroups.reduce((acc, resultGroup: CalculationResults) => {
				acc[resultGroup.value] = {
					value: resultGroup.value,
					pageNumber: 1,
					pageSize: this.perPage,
					sortBy: "",
					sortDataType: DataType.none,
					sortDesc: false
				}
				return acc
			}, {} as { [key: string]: CurrentPageDetails })
		}
	}

	@Watch("filteredResultGroups")
	private resultGroupsChanged (): void {
		this.filteredResultGroups.forEach((resultGroup, index) => {
			if (!this.groupPages[resultGroup.value]) {
				Vue.set(this.groupPages, resultGroup.value, {
					label: resultGroup.value,
					pageNumber: 1
				})
			}
		})
		this.$emit("filteredResultsGroups", this.filteredResultGroups)
	}

	@Watch("additionalFilters", { deep: true })
	private additionalFiltersChanged (newValue: AdvancedSearchFilter[], oldValue: AdvancedSearchFilter[]): void {
		if (!isEqual(newValue, oldValue)) {
			this.applyFilters()
		}
	}

	/**
	 * Get field Label value for field ID
	 */
	public getFieldLabel (fieldId: string, caseDetails: CaseDetails): string | null {
		if (!caseDetails.fields[fieldId]) {
			return null
		}

		return caseDetails.fields[fieldId].label
	}

	private groupByText (calculation: any, groupCalculationResult: any): string {
		if (calculation.groupByKeyLabels) {
			const calculationKeys = Object.keys(calculation.groupByKeyLabels).filter(k => groupCalculationResult && groupCalculationResult[k] !== undefined)
			return calculationKeys.map(k => `${calculation.groupByKeyLabels[k]}: ${groupCalculationResult[k] !== null
				? formatInsight.methods.roundNumber(groupCalculationResult[k], 2)
				: 0}`)
				.join(", ")
		}
		return ""
	}

	private groupTotalsByText (calculationTotal: InsightCalculationTotal, result: any): string {
		if (calculationTotal.calculationList && calculationTotal.calculationList.length > 0) {
			const calculation = this.tableCalculations.find((calc: any) => calc.key === calculationTotal.calculationList[0])
			return calculation ? this.groupByText(calculation, result) : ""
		}
		return ""
	}

	private getCurrentPage (group: any): number {
		if (this.groupPages[group.value]) {
			if (this.groupPages[group.value].pageNumber && this.groupPages[group.value].pageNumber !== undefined) {
				return this.groupPages[group.value].pageNumber ?? 1
			}
		}

		return 1
	}

	private resultsCountText (group: FilteredCalculationResults): string {
		const total = group.totalRecords ?? group.results.length
		const resultWord = total === 1 ? "result" : "results"

		const currentPage = this.getCurrentPage(group)
		const firstOnPage = ((currentPage - 1) * this.perPage) + 1

		if (total <= this.perPage) {
			return `Showing ${total} ${resultWord}.`
		}

		const lastOnPage = Math.min(currentPage * this.perPage, total)

		return `Showing ${firstOnPage}-${lastOnPage} of ${total} ${resultWord}.`
	}

	private applyFilters () {
		const filters = [
			...this.additionalFilters,
			...this.filters
		]

		this.groupPages = {}
		this.$emit("reloadInsightByClauses", this.createClausesFromFilters(filters))
	}

	private createClausesFromFilters (filters: AdvancedSearchFilter[]): Clause[] {
		return filters.filter(filter => !!filter).map(filter => {
			const isOrClause = filter.key === this.orClauseKey
			return {
				field: isOrClause ? "" : filter.key,
				label: isOrClause ? "" : filter.columnLabel ?? "",
				operator: isOrClause ? Operator.Equals : filter.operator,
				value: isOrClause ? null : filter.value,
				orClause: isOrClause,
				subClauses: !!filter.orFilters && filter.orFilters.length > 0 ? this.createClausesFromFilters(filter.orFilters) : undefined,
				fieldDataType: isOrClause ? 0 : filter.column?.fieldDataType ?? 0,
				// Custom field columns are keyed "fields.<id>" and must be flagged dynamic.
				dynamicField: isOrClause ? false : filter.column?.dynamicField ?? false,
				dynamicValue: isOrClause ? false : filter.dynamicValue ?? false
			}
		})
	}

	private forecastClicked (caseItem: { scheduling: any }) {
		if (!caseItem.scheduling) return

		this.viewScheduleInstance = {
			schedule: {
				id: caseItem.scheduling.scheduleId,
				title: caseItem.scheduling.scheduleTitle
			} as Schedule,
			date: new Date(caseItem.scheduling.instanceDate)
		}
	}

	private async scheduleInstanceUpdated ({ scheduleId, date, fieldValues }: { scheduleId: string, date: Date, fieldValues: { [ key: string ]: { label: string, value: any }} }) {
		const dateString = `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`

		const updateInstance = await api.scheduleInstance(scheduleId, date)

		if (this.resultGroups) {
			this.resultGroups.forEach(group => {
				if (!group.results) return

				group.results.filter(r => r.scheduling && r.scheduling.scheduleId === scheduleId && r.scheduling.instanceDate === dateString)
					.forEach(insightRecord => {
						applyFieldChanges(insightRecord, fieldValues)
						if (updateInstance.updatedScheduleInstance && updateInstance.updatedScheduleInstance.users) {
							insightRecord.users = updateInstance.updatedScheduleInstance.users
						}
					})
			})
		}
	}

	private roundCalculationValue (number: string) {
		return formatInsight.methods.roundNumber(number, 2)
	}

	private async caseEdited () {
		if (this.insight.id) {
			await api.insightMarkAsCacheExpired(this.insight.id)
		}
	}

	private onPageClick (group: any) {
		this.$emit("loadNextPage", group.value, this.groupPages[group.value])
	}

	@Emit("reloadInsightBySort")
	private setSortDetails (details: CurrentPageDetails) {
		this.groupPages[details.tableId ?? details.value] = {
			value: details.value,
			pageNumber: 1,
			pageSize: this.perPage,
			sortBy: details.sortBy,
			sortDesc: details.sortDesc,
			sortDataType: details.sortDataType
		}

		return this.groupPages[details.value]
	}
}

